<template>
  <div>
    <el-card class="mb-16">
      <div class="flex-row align-start" style="gap: 16px; flex-wrap: wrap;">
        <div>
          <label class="label">選擇群組</label>
          <el-select
            v-model="selectedGroupId"
            clearable
            filterable
            placeholder="不選則顯示總覽"
            style="width: 260px;"
            @change="onGroupChange"
          >
            <el-option
              v-for="g in sortedGroups"
              :key="g._id"
              :value="g._id"
              :label="g.notes || '（未命名群組）'"
            />
          </el-select>
        </div>

        <div v-if="selectedGroupData" class="group-meta">
          <div>成員人數：{{ selectedGroupData.userList ? selectedGroupData.userList.length : 0 }}</div>
          <div>建立時間：{{ selectedGroupData.created_at | formatDate }}</div>
        </div>

        <el-button type="primary" size="small" icon="el-icon-plus" @click="openGroupDialog()">新增群組</el-button>
        <el-button v-if="selectedGroupData" size="small" @click="openGroupDialog(selectedGroupData)">編輯群組名稱</el-button>

        <el-switch v-model="showDisabled" active-text="顯示已停用用戶" style="margin-left: auto;" />

        <el-button v-if="selectedGroupId" type="primary" :loading="saving" @click="save">儲存</el-button>
      </div>
    </el-card>

    <!-- 總覽：未選擇群組時，唯讀顯示所有使用者 x 所有群組的對應狀態 -->
    <el-card v-if="!selectedGroupId" v-loading="loading">
      <el-table
        v-if="!loading"
        ref="overviewTable"
        :data="overviewRows"
        border
        size="mini"
        style="width: 100%;"
        max-height="600"
      >
        <el-table-column label="用戶名稱" width="380" sortable :sort-method="sortByName" fixed>
          <template slot-scope="{ row }">
            <span>{{ row.name }}</span>
            <!--span style="font-size: 11px; color: #909399; margin-left: 16px;">{{ row.email }}</span-->
          </template>
        </el-table-column>
        <el-table-column prop="roles" label="權限" width="90" sortable :sort-method="sortByRole" fixed />
        <el-table-column
          v-for="g in sortedGroups"
          :key="g._id"
          :label="g.notes || '（未命名群組）'"
          :width="groupColumnWidth"
          align="center"
          sortable
          :sort-method="(a, b) => sortByMembership(a, b, g._id)"
          :render-header="renderGroupHeader"
        >
          <template slot-scope="{ row }">
            <el-checkbox :value="row.membership[g._id]" disabled />
          </template>
        </el-table-column>
      </el-table>
      <div v-if="!loading" class="overview-hint">
        共 {{ overviewRows.length }} 位用戶、{{ sortedGroups.length }} 個群組（僅供檢視，選擇群組後可編輯成員）
      </div>
    </el-card>

    <!-- 編輯：已選擇群組時，列出所有使用者供勾選成員 -->
    <el-card v-else v-loading="loading">
      <el-table
        v-if="!loading"
        ref="editTable"
        :data="visibleEditRows"
        border
        size="mini"
        style="width: 100%;"
        max-height="600"
      >
        <el-table-column label="用戶名稱" width="380" sortable :sort-method="sortByName" fixed>
          <template slot-scope="{ row }">
            <span>{{ row.name }}</span>
            <!--span style="font-size: 11px; color: #909399; margin-left: 16px;">{{ row.email }}</span-->
          </template>
        </el-table-column>
        <el-table-column prop="roles" label="權限" width="90" sortable :sort-method="sortByRole" fixed />
        <el-table-column label="群組成員" width="100" align="center" sortable :sort-method="sortByInGroup">
          <template slot-scope="{ row }">
            <el-checkbox v-model="row.inGroup" />
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <el-dialog
      :title="groupForm._id ? '編輯群組名稱' : '新增群組'"
      :visible.sync="groupDialogVisible"
      width="480px"
      :close-on-click-modal="false"
    >
      <el-form :model="groupForm" label-position="top">
        <el-form-item label="群組備註 (必填)">
          <el-input v-model="groupForm.notes" placeholder="例如：某專案緊急連絡組" />
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="groupDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="groupSaving" @click="saveGroupMeta">確認儲存</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
const ROLE_ORDER = { admin: 0, thsr: 1, occ: 2, decl: 3, client: 4 };

function roleOrder(role) {
  return ROLE_ORDER[role] ?? 5;
}

function toIdSet(arr) {
  return new Set((arr || []).map(id => (typeof id === 'object' ? id._id : id)));
}

export default {
  name: 'AlertGroupManagement',
  filters: {
    formatDate(val) {
      if (!val) return '—';
      return new Date(val).toLocaleString('zh-TW');
    },
  },
  data() {
    return {
      groupColumnWidth: 140,
      loading: false,
      alertGroupList: [],
      allClients: [],
      showDisabled: false,
      selectedGroupId: '',
      editRows: [],
      saving: false,
      groupDialogVisible: false,
      groupSaving: false,
      groupForm: {
        _id: null,
        notes: '',
      },
    };
  },
  computed: {
    sortedGroups() {
      return [...this.alertGroupList].sort((a, b) => (a.notes || '').localeCompare(b.notes || '', 'zh-Hant'));
    },
    selectedGroupData() {
      return this.alertGroupList.find(g => g._id === this.selectedGroupId) || null;
    },
    // 預設隱藏已停用用戶，但仍保留在 allClients 中以免 save() 誤刪其既有告警設定
    visibleClients() {
      return this.showDisabled
        ? this.allClients
        : this.allClients.filter(u => u.roles !== 'disabled');
    },
    visibleEditRows() {
      return this.showDisabled ? this.editRows : this.editRows.filter(r => r.roles !== 'disabled');
    },
    overviewRows() {
      const groups = this.sortedGroups;
      const groupSets = groups.map(g => toIdSet(g.userList));

      const rows = this.visibleClients.map(u => {
        const membership = {};
        groups.forEach((g, idx) => {
          membership[g._id] = groupSets[idx].has(u._id);
        });
        return {
          _id: u._id,
          name: u.name || '（無名稱）',
          email: u.email || '',
          roles: u.roles || '',
          membership,
        };
      });

      const firstGroupId = groups.length ? groups[0]._id : null;
      rows.sort((a, b) => {
        if (firstGroupId) {
          const diff = (b.membership[firstGroupId] ? 1 : 0) - (a.membership[firstGroupId] ? 1 : 0);
          if (diff !== 0) return diff;
        }
        return roleOrder(a.roles) - roleOrder(b.roles);
      });

      return rows;
    },
  },
  watch: {
    selectedGroupId() {
      this.refreshTableLayout();
    },
    showDisabled() {
      this.refreshTableLayout();
    },
  },
  async mounted() {
    this.loading = true;
    try {
      await Promise.all([this.fetchAlertGroups(), this.fetchAllClients()]);
    } finally {
      this.loading = false;
      this.refreshTableLayout();
    }
  },
  methods: {
    // fixed 欄位在資料非同步載入、欄位數量變動後常會有表頭/內容錯位的問題（Element UI 舊版已知問題），
    // 官方建議的解法是在版面穩定後手動呼叫 doLayout() 強制重新計算固定欄位的位置與高度
    refreshTableLayout() {
      this.$nextTick(() => {
        const table = this.$refs.overviewTable || this.$refs.editTable;
        if (table && typeof table.doLayout === 'function') {
          table.doLayout();
        }
      });
    },
    // 群組名稱可能很長，強制表頭單行截斷＋hover 顯示完整文字，避免多行表頭撐高造成凍結欄位版面錯位
    // 用 inline style（而非 scoped class）是因為 render-header 是在 el-table 自身的 render context 執行，
    // 我們元件的 scoped 樣式 data-v 屬性不會被套用到這裡產生的節點上
    // maxWidth 刻意小於欄寬，保留排序 caret 圖示的空間；若設成 100% 會把 caret 擠到換行，導致整個表頭列被撐高、
    // 連帶讓沒有這個問題的「用戶名稱／權限」凍結欄位表頭也跟著變高，看起來像格子被切成兩行
    renderGroupHeader(h, { column }) {
      return h('span', {
        attrs: { title: column.label },
        style: {
          display: 'inline-block',
          maxWidth: `${this.groupColumnWidth - 44}px`,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          verticalAlign: 'middle',
        },
      }, column.label);
    },
    async fetchAlertGroups() {
      try {
        const res = await this.$axios({
          category: 'alerts',
          method: 'get',
          apiName: 'getAllAlertGroup',
        });
        this.alertGroupList = Array.isArray(res.data) ? res.data : [];
      } catch (e) {
        this.$message.error('載入告警群組失敗');
      }
    },
    async fetchAllClients() {
      try {
        const res = await this.$axios({
          category: 'admin',
          method: 'get',
          apiName: 'client',
        });
        this.allClients = Array.isArray(res.data) ? res.data : [];
      } catch (e) {
        console.error('Fetch clients error', e);
      }
    },
    onGroupChange() {
      this.buildEditRows();
    },
    sortByName(a, b) {
      return (a.name || '').localeCompare(b.name || '', 'zh-Hant');
    },
    sortByRole(a, b) {
      return roleOrder(a.roles) - roleOrder(b.roles);
    },
    sortByMembership(a, b, groupId) {
      return (a.membership[groupId] ? 1 : 0) - (b.membership[groupId] ? 1 : 0);
    },
    sortByInGroup(a, b) {
      return (a.inGroup ? 1 : 0) - (b.inGroup ? 1 : 0);
    },
    buildEditRows() {
      const memberSet = toIdSet(this.selectedGroupData ? this.selectedGroupData.userList : []);

      const rows = this.allClients.map(u => ({
        _id: u._id,
        name: u.name || '（無名稱）',
        email: u.email || '',
        roles: u.roles || '',
        inGroup: memberSet.has(u._id),
      }));

      rows.sort((a, b) => {
        const diff = (b.inGroup ? 1 : 0) - (a.inGroup ? 1 : 0);
        if (diff !== 0) return diff;
        return roleOrder(a.roles) - roleOrder(b.roles);
      });

      this.editRows = rows;
    },
    openGroupDialog(group = null) {
      if (group) {
        this.groupForm = {
          _id: group._id,
          notes: group.notes || '',
        };
      } else {
        this.groupForm = {
          _id: null,
          notes: '',
        };
      }
      this.groupDialogVisible = true;
    },
    async saveGroupMeta() {
      if (!this.groupForm.notes) {
        this.$message.warning('請填寫群組備註');
        return;
      }
      this.groupSaving = true;
      try {
        const isEdit = !!this.groupForm._id;
        // 重新命名時沿用既有成員名單，避免覆蓋成空陣列；新增時成員名單留空，後續在編輯畫面勾選
        const existingGroup = isEdit ? this.alertGroupList.find(g => g._id === this.groupForm._id) : null;
        const existingUserList = existingGroup
          ? (existingGroup.userList || []).map(u => (typeof u === 'object' ? u._id : u))
          : [];

        const res = await this.$axios({
          category: 'alerts',
          method: isEdit ? 'put' : 'post',
          apiName: isEdit ? `alertGroup/${this.groupForm._id}` : 'alertGroup',
          data: {
            notes: this.groupForm.notes,
            userList: existingUserList,
          },
        });

        this.$message.success(isEdit ? '更新成功' : '新增成功');
        this.groupDialogVisible = false;
        await this.fetchAlertGroups();

        if (isEdit) {
          if (this.selectedGroupId === this.groupForm._id) {
            this.buildEditRows();
          }
        } else {
          // 後端未回傳新建群組 id 時，退而以剛送出的備註在重新整理後的清單中比對
          const createdId = res && res.data && res.data._id
            ? res.data._id
            : [...this.alertGroupList]
              .filter(g => g.notes === this.groupForm.notes)
              .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0]?._id;
          if (createdId) {
            this.selectedGroupId = createdId;
            this.onGroupChange();
          }
        }
      } catch (e) {
        console.error(e);
        this.$message.error('儲存失敗');
      } finally {
        this.groupSaving = false;
      }
    },
    async save() {
      if (!this.selectedGroupId || !this.selectedGroupData) return;
      const response = await this.$confirmEdit('此操作將更新群組成員設定');
      if (response !== 'confirm') return;

      this.saving = true;
      try {
        await this.$axios({
          category: 'alerts',
          method: 'put',
          apiName: `alertGroup/${this.selectedGroupId}`,
          data: {
            notes: this.selectedGroupData.notes,
            userList: this.editRows.filter(r => r.inGroup).map(r => r._id),
          },
        });

        await this.fetchAlertGroups();
        this.buildEditRows();
        this.$notifySuccess('儲存成功');
      } catch (e) {
        this.$notifyError(e);
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.flex-row { display: flex; flex-direction: row; }
.align-start { align-items: flex-start; }
.label { display: block; margin-bottom: 6px; font-weight: bold; color: #606266; font-size: 14px; }
.mb-16 { margin-bottom: 16px; }
.group-meta { font-size: 13px; color: #606266; line-height: 1.6; padding-top: 22px; }
.overview-hint { margin-top: 8px; color: #909399; font-size: 12px; }

/*
 * fixed 欄位橫向捲動時錯位的真正原因：全域 bulma.css 的 .is-hidden { display: none !important }
 * 蓋掉了 Element UI 自己給「被固定欄位複製到主表格那份佔位 cell」用的 .is-hidden 規則
 * （Element 原意是 display: table-cell + 內容 visibility: hidden，只是視覺上隱藏、仍保留欄寬）。
 * cell 被整個 display:none 掉之後，瀏覽器的 table-layout:fixed 會把後面欄位往前遞補，
 * 造成 colgroup 欄寬跟實際儲存格對不齊，也就是使用者看到的「格子被切分/跑版」。
 * 這裡把它改回 Element 原本要的 display，讓欄位對齊恢復正常。
 */
::v-deep .el-table__cell.is-hidden {
  display: table-cell !important;
}
</style>
